import { CheckCircle2, ChevronLeft, ScanLine } from "lucide-react";
import { easeOut, progress, type DemoScene } from "./demoScenario";
import { FocusTrace } from "./FocusTrace";
import { FRIDGE_PHOTO } from "./featureScenarios";

type Props = { scene: DemoScene; time: number; still: boolean };
type Nutrient = { name: string; amount: number; unit: string; low: number; target: number; high: number; suggested?: boolean };
const STATUS = { low: "Too Low", good: "On Target", high: "Too High" };
// Illustrative profile, kept consistent across the three filters (67 nutrients).
const NUTRIENTS: Record<keyof typeof STATUS, Nutrient[]> = {
  good: [
    { name: "Fiber", amount: 38, unit: "g", low: 7, target: 9, high: 15, suggested: true },
    { name: "Vitamin D", amount: 631, unit: "IU", low: 65, target: 217, high: 868 },
    { name: "Vitamin C", amount: 53, unit: "mg", low: 16, target: 20, high: 98 },
  ],
  low: [
    { name: "Protein", amount: 60, unit: "g", low: 80, target: 100, high: 180 },
    { name: "Potassium", amount: 1800, unit: "mg", low: 2800, target: 3500, high: 5000, suggested: true },
    { name: "Calcium", amount: 520, unit: "mg", low: 800, target: 1000, high: 2500 },
  ],
  high: [
    { name: "Sodium", amount: 2600, unit: "mg", low: 500, target: 1500, high: 2300 },
    { name: "Saturated fat", amount: 24, unit: "g", low: 0, target: 10, high: 20 },
    { name: "Added sugar", amount: 58, unit: "g", low: 0, target: 25, high: 50 },
  ],
};

function Header({ title }: { title: string }) {
  return <div className="nd-native-header"><div className="nd-back"><ChevronLeft /></div><h3>{title}</h3></div>;
}

export function FridgePhoto() {
  // Reuse the exact photograph in the approved screenshot, with an SVG viewport
  // selecting only its photo area. All surrounding UI is live HTML.
  return <svg className="nd-fridge-photo" viewBox="38 408 776 840" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    <image href={FRIDGE_PHOTO} width="853" height="1844" />
  </svg>;
}

export function NutrientStatus(props: Props) {
  const status = props.scene.nutrientStatus ?? "good";
  return <><Header title="Nutrients Eaten" /><div className="nd-deep-body nd-nutrient-body">
    <div className="nd-filter-panel"><div className="nd-period"><span>Today</span><span>Past 2 Weeks</span></div>
      <div className="nd-nutrient-filters">{(["low", "good", "high"] as const).map((tone, index) =>
        <FocusTrace key={tone} {...props} target={`filter-${tone}`} className={`nd-nutrient-filter nd-${tone} ${status === tone ? "nd-active-filter" : ""}`}>
          <strong>{STATUS[tone]}</strong><span>{[8, 32, 27][index]}</span>
        </FocusTrace>,
      )}</div>
    </div>
    <div key={status} className="nd-nutrient-list">{NUTRIENTS[status].map((nutrient, index) => {
      const fill = props.still ? 1 : easeOut(progress(props.time, props.scene.start + 480 + index * 160, 950));
      const maximum = Math.max(nutrient.high * 1.35, nutrient.amount * 1.18);
      const position = (amount: number) => `${amount / maximum * 100}%`;
      return <div key={nutrient.name} className="nd-card nd-nutrient-card">
        <div className="nd-nutrient-title"><h4>{nutrient.name}</h4><span className={`nd-${status}`}>{nutrient.amount} {nutrient.unit}</span></div>
        <div className={`nd-nutrient-badge nd-${status}`}><CheckCircle2 />{STATUS[status]}</div>
        <div className="nd-range" style={{ background: `linear-gradient(to right, #f9d9bd 0% ${position(nutrient.low)}, #c6e7e2 ${position(nutrient.low)} ${nutrient.suggested ? "100%" : position(nutrient.high)}, #f1b1cc ${nutrient.suggested ? "100%" : position(nutrient.high)} 100%)` }}>
          <span className="nd-range-rail" /><span className="nd-range-fill" style={{ width: position(nutrient.amount), scale: `${fill} 1` }} />
          <i className="nd-low" style={{ left: position(nutrient.low) }} /><i className="nd-good" style={{ left: position(nutrient.target) }} /><i className={`nd-high ${nutrient.suggested ? "nd-suggested-limit" : ""}`} style={{ left: position(nutrient.high) }} />
        </div>
        <div className="nd-range-labels"><span className="nd-low">Low<b>{nutrient.low}</b></span><span className="nd-good">Target<b>{nutrient.target}</b></span><span className="nd-high">High{nutrient.suggested ? " (suggested)" : ""}<b>{nutrient.high}</b></span></div>
      </div>;
    })}</div>
  </div></>;
}

export function FridgeAnalysis(props: Props) {
  const amount = progress(props.time, props.scene.start, props.scene.end - props.scene.start);
  return <><Header title="Scan your fridge" /><div className="nd-deep-body nd-fridge-analysis"><div className="nd-analysis-canvas">
    <div className="nd-analysis-photo"><FridgePhoto /><div className="nd-scan-line" style={{ top: `${10 + amount * 80}%` }} /></div>
    <div className="nd-analysis-progress"><ScanLine /><h4>Analyzing your fridge</h4><p>Finding food for your nutrient gaps</p><div className="nd-loading-track"><span style={{ transform: `scaleX(${amount})` }} /></div></div>
  </div></div></>;
}

export function FridgeResult(props: Props) {
  // A gentle scroll keeps the photograph, then the recommendation, in focus.
  const scroll = props.still ? 1 : easeOut(progress(props.time, props.scene.start + 2300, 1200));
  return <><Header title="Scan your fridge" /><div className="nd-deep-body nd-fridge-results">
    <div className="nd-fridge-scroll" style={{ transform: `translateY(${-scroll * 4.5}em)` }}>
      <div className="nd-fridge-result-photo"><FridgePhoto /></div>
      <div className="nd-fridge-intro"><h4>Fridge guidance</h4><p>You need more of 25 nutrients right now. Here’s what’s in your fridge that can help:</p></div>
      <div className="nd-card nd-fridge-recommendation"><span className="nd-fridge-covered"><CheckCircle2 /> Covered by your fridge</span><h4>6 eggs</h4><p>That will give you 38 g of the 37 g of protein that you need right now.</p></div>
    </div>
  </div></>;
}
